import React, { useState } from 'react';
import { 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  ComposedChart
} from 'recharts';

interface DataPoint {
  [key: string]: string | number;
}

interface LineConfig {
  key: string;
  color: string;
  name: string;
  type?: 'line' | 'area';
  yAxisId?: 'left' | 'right';
}

interface LineChartProps {
  data: DataPoint[];
  xAxisKey: string;
  lines: LineConfig[];
  title?: string;
  height?: number;
  showGrid?: boolean;
  showDots?: boolean;
  dualAxis?: boolean;
}

// Custom tooltip for better readability
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-4 py-3 shadow-lg rounded-lg border border-gray-200">
        <p className="font-medium text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-semibold text-gray-900">
              {entry.name.includes('Revenue') || entry.name.includes('$')
                ? `$${Number(entry.value).toLocaleString()}`
                : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const LineChart: React.FC<LineChartProps> = ({ 
  data, 
  xAxisKey, 
  lines, 
  title, 
  height = 300,
  showGrid = true,
  showDots = true,
  dualAxis = true
}) => {
  // State for toggling lines visibility
  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    new Set(lines.map(l => l.key))
  );

  // Toggle line visibility
  const toggleLine = (key: string) => {
    setVisibleLines(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        // Don't allow hiding all lines
        if (newSet.size > 1) {
          newSet.delete(key);
        }
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Check if we have any data
  const hasData = data && data.length > 0;
  
  // Get visible lines
  const activeLines = lines.filter(l => visibleLines.has(l.key));
  
  // Calculate summary stats for visible lines only
  const summaryStats = lines.map(line => {
    const values = data.map(d => Number(d[line.key]) || 0);
    const total = values.reduce((sum, v) => sum + v, 0);
    const max = Math.max(...values);
    return { ...line, total, max, isVisible: visibleLines.has(line.key) };
  });

  // Determine if we need dual axis (when values have very different scales)
  const visibleStats = summaryStats.filter(s => s.isVisible);
  const maxValues = visibleStats.map(s => s.max).filter(v => v > 0);
  const needsDualAxis = dualAxis && visibleStats.length >= 2 && maxValues.length >= 2 &&
    (Math.max(...maxValues) / Math.min(...maxValues) > 10);

  if (!hasData) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        {title && (
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        )}
        <div className="flex flex-col items-center justify-center" style={{ height }}>
          <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-gray-500 text-sm mt-4">No trend data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {title && (
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      )}
      
      {/* Toggle buttons and summary stats */}
      <div className="flex flex-wrap justify-center gap-4 mb-4">
        {summaryStats.map((stat, index) => (
          <button
            key={index}
            onClick={() => toggleLine(stat.key)}
            className={`flex flex-col items-center px-4 py-2 rounded-lg border-2 transition-all ${
              stat.isVisible 
                ? 'border-current bg-opacity-10' 
                : 'border-gray-200 bg-gray-50 opacity-50'
            }`}
            style={{ 
              borderColor: stat.isVisible ? stat.color : undefined,
              backgroundColor: stat.isVisible ? `${stat.color}10` : undefined
            }}
          >
            <p className="text-lg font-bold" style={{ color: stat.isVisible ? stat.color : '#9CA3AF' }}>
              {stat.name.includes('Revenue') || stat.name.includes('$')
                ? `$${stat.total.toLocaleString()}`
                : stat.total}
            </p>
            <p className="text-xs text-gray-500">{stat.name}</p>
            <p className="text-xs mt-1" style={{ color: stat.isVisible ? stat.color : '#9CA3AF' }}>
              {stat.isVisible ? '● Showing' : '○ Hidden'}
            </p>
          </button>
        ))}
      </div>
      
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 5, right: needsDualAxis ? 60 : 30, left: 20, bottom: 5 }}>
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#E5E7EB" 
              vertical={false}
            />
          )}
          <XAxis 
            dataKey={xAxisKey} 
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          
          {/* Left Y-axis (for revenue/larger values) */}
          <YAxis 
            yAxisId="left"
            tick={{ fontSize: 12, fill: '#6B7280' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => {
              if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
              return `$${value}`;
            }}
          />
          
          {/* Right Y-axis (for bookings/smaller values) - only if needed */}
          {needsDualAxis && (
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: '#10B981' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => Math.round(value).toString()}
            />
          )}
          
          <Tooltip content={<CustomTooltip />} />
          
          {activeLines.map((line, index) => {
            // First line uses left axis, second uses right (if dual axis)
            const yAxisId = needsDualAxis && index > 0 ? 'right' : 'left';
            
            return (
              <React.Fragment key={line.key}>
                {/* Add gradient area under the line */}
                <defs>
                  <linearGradient id={`gradient-${line.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={line.color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={line.color} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey={line.key}
                  stroke="none"
                  fill={`url(#gradient-${line.key})`}
                  yAxisId={yAxisId}
                />
                <Line
                  type="monotone"
                  dataKey={line.key}
                  stroke={line.color}
                  name={line.name}
                  strokeWidth={3}
                  dot={showDots ? { fill: line.color, strokeWidth: 2, r: 4 } : false}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                  yAxisId={yAxisId}
                />
              </React.Fragment>
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
      
      {/* Legend */}
      <div className="flex justify-center gap-6 mt-2">
        {lines.map((line, index) => (
          <div 
            key={index} 
            className={`flex items-center gap-2 cursor-pointer ${!visibleLines.has(line.key) ? 'opacity-40' : ''}`}
            onClick={() => toggleLine(line.key)}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: line.color }}
            />
            <span className="text-sm text-gray-700">{line.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LineChart;
